import React, {memo} from 'react';

import moment from 'moment-tz';
import {browserHistory, WithRouterProps} from 'react-router';

import {UserAvatar} from 'app/components/general/avatars';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {
  StyledCompactItemHeaderDiv,
  StyledCompactItemHeaderSpan,
} from 'app/general/bootstrap/utils';
import {
  SingleActionButton,
  SpacingDummyActionButton,
} from 'app/general/widgets/SingleActionButton';
import {USERS_HOME} from 'app/urls';
import {useDeleteInvitation} from 'app/user/api';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import type {InvitationSerializedType} from 'sweet-types';

interface InvitationHeaderProps {
  shared_item: InvitationSerializedType['shared_item'] | null;
  permission?: 'view' | 'edit';
}

const InvitationHeader = ({shared_item, permission}: InvitationHeaderProps) => {
  if (shared_item === null) {
    return (
      <StyledCompactItemHeaderSpan>
        {gt('Deleted Item')}
      </StyledCompactItemHeaderSpan>
    );
  }

  const ct =
    shared_item.content_type !== undefined
      ? getContentType(shared_item.content_type)
      : {icon: ''};

  const icon = <i className={`fa ${ct.icon}`} />;

  if (shared_item.content_type === 'accountprofile') {
    return (
      <StyledCompactItemHeaderSpan>
        {icon} {gt('This Account')}
      </StyledCompactItemHeaderSpan>
    );
  } else if (
    'account_wide' in shared_item &&
    shared_item.account_wide === true &&
    permission === 'edit'
  ) {
    return (
      <StyledCompactItemHeaderSpan>
        {icon} {gt('Super Manager')}
      </StyledCompactItemHeaderSpan>
    );
  } else if (
    'account_wide' in shared_item &&
    shared_item.account_wide === true &&
    permission === 'view'
  ) {
    return (
      <StyledCompactItemHeaderSpan>
        {icon} {gt('Super Teammate')}
      </StyledCompactItemHeaderSpan>
    );
  } else {
    return (
      <FullHTMLLink html_url={shared_item.html_url}>
        <StyledCompactItemHeaderSpan>
          {icon} {getObjectName(shared_item, shared_item.content_type, true)}
        </StyledCompactItemHeaderSpan>
      </FullHTMLLink>
    );
  }
};

type InvitationItemProps = {object: InvitationSerializedType};

export const InvitationItem = memo<InvitationItemProps>(
  ({object}: InvitationItemProps & WithRouterProps) => {
    const [deleteInvitation] = useDeleteInvitation();

    return (
      <div className="row border-bottom py-2">
        <div className="col">
          <div className="row">
            <StyledCompactItemHeaderDiv>
              <InvitationHeader
                shared_item={object.shared_item}
                permission={object.permission}
              />
            </StyledCompactItemHeaderDiv>

            <div className="col mt-2 mt-lg-0 col-lg-2 text-truncate d-flex align-items-center">
              <UserAvatar
                tooltipTitle={gt('Invitation sent by')}
                user={object.from_user ?? undefined}
                showCard
              />
              &nbsp;
              <LazyTooltip
                tooltip={
                  <small>{moment(object.created_at).format('lll')}</small>
                }
              >
                <small>{moment(object.created_at).fromNow()}</small>
              </LazyTooltip>
            </div>
          </div>
        </div>

        <div className="col-auto pl-0 d-flex align-items-center">
          {object.shared_item != null &&
          object.shared_item.can_edit === true ? (
            <SingleActionButton
              action={{
                text: gt('Remove'),
                icon: 'fa fa-times',
                key: 'remove',
                callback: () =>
                  deleteInvitation({url: object.url}).then(
                    () =>
                      object.content_type === 'accountprofile' &&
                      browserHistory.push(USERS_HOME)
                  ),
              }}
            />
          ) : (
            <SpacingDummyActionButton />
          )}
        </div>
      </div>
    );
  }
);
